import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import {
  FolderSpecial as ProjectIcon,
  LocationOn as LocationIcon,
  GraphicEq as AudioIcon,
  Sensors as SensorIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { MinimalBirdIcon as SpeciesIcon } from './MinimalBirdIcon';
import { BioProject, BioSite } from '../bioacousticMockData';
import SpeciesDistributionDonut from './SpeciesDistributionDonut';

interface ProjectLevelSectionProps {
  project: BioProject;
  activeSiteId: string;
  onSelectSite: (site: BioSite) => void;
}

const ProjectLevelSection: React.FC<ProjectLevelSectionProps> = ({
  project,
  activeSiteId,
  onSelectSite,
}) => {
  return (
    <Box id="project-level-section" sx={{ mb: 6 }}>
      {/* Level Header Banner */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box
              sx={{
                bgcolor: 'rgba(16, 185, 129, 0.15)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '4px',
                px: 1,
                py: 0.25,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <ProjectIcon sx={{ fontSize: 13, color: '#10B981' }} />
              <Typography sx={{ fontSize: '10px', fontWeight: 800, color: '#10B981', letterSpacing: '1px' }}>
                LEVEL 1 : PROJECT LEVEL
              </Typography>
            </Box>
          </Box>
          <Typography
            sx={{
              fontSize: '24px',
              fontWeight: 800,
              color: '#FFFFFF',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
            }}
          >
            {project.project_name}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: '#666666', mt: 0.25 }}>
            Aggregate landscape bioacoustic monitoring, species richness & threat assessment
          </Typography>
        </Box>
      </Box>

      {/* Top Row: Left 2x2 KPI Grid + Right Donut Chart */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, mb: 3 }}>
        {/* Left Side: 2x2 KPI Grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: 2, height: '100%' }}>
          {[
            {
              label: 'Total Species Identified',
              value: project.totalSpecies,
              sub: 'Across all project sites',
              icon: SpeciesIcon,
              color: '#10B981',
            },
            {
              label: 'Total Audio Files',
              value: project.totalAudioFiles.toLocaleString(),
              sub: 'Processed & analyzed',
              icon: AudioIcon,
              color: '#00D4FF',
            },
            {
              label: 'Monitored Sites',
              value: project.totalSites,
              sub: 'Active acoustic stations',
              icon: LocationIcon,
              color: '#F59E0B',
            },
            {
              label: 'Active Deployments',
              value: project.totalDeployments,
              sub: 'Autonomous audio sensors',
              icon: SensorIcon,
              color: '#8B5CF6',
            },
          ].map((kpi) => {
            const IconComp = kpi.icon;
            return (
              <Box
                key={kpi.label}
                sx={{
                  background: 'rgba(255, 255, 255, 0.02)',
                  border: '1px solid #1E1E1E',
                  borderRadius: '12px',
                  p: '16px 18px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    borderColor: `${kpi.color}55`,
                    background: 'rgba(255, 255, 255, 0.04)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography sx={{ fontSize: '10px', color: '#777777', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                    {kpi.label}
                  </Typography>
                  <IconComp sx={{ fontSize: 20, color: kpi.color, opacity: 0.85 }} />
                </Box>
                <Typography sx={{ fontSize: '28px', fontWeight: 800, color: '#FFFFFF', lineHeight: 1.1, my: 'auto' }}>
                  {kpi.value}
                </Typography>
                <Typography sx={{ fontSize: '10px', color: '#555555', mt: 1 }}>
                  {kpi.sub}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Right Side: Donut Chart */}
        <Box sx={{ height: '100%' }}>
          <SpeciesDistributionDonut
            data={project.threatDistribution}
            title="Species Distribution by Threat Level (In-Situ)"
            height={300}
          />
        </Box>
      </Box>

      {/* List of Sites Header & Grid Cards */}
      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Box>
            <Typography sx={{ fontSize: '11px', fontWeight: 800, color: '#10B981', letterSpacing: '1px', textTransform: 'uppercase' }}>
              Sites Directory
            </Typography>
            <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#FFFFFF' }}>
              Select a Site to Explore ({project.sites.length})
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '11px', color: '#555555' }}>
            Click on any site to view site-level diurnal trends & deployments
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
          {project.sites.map((site) => {
            const isSelected = site.site_id === activeSiteId;
            return (
              <Box
                key={site.site_id}
                onClick={() => onSelectSite(site)}
                sx={{
                  background: isSelected ? 'rgba(16, 185, 129, 0.08)' : 'rgba(10, 10, 10, 0.85)',
                  border: `1px solid ${isSelected ? '#10B981' : '#1E1E1E'}`,
                  borderRadius: '10px',
                  p: 2,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#10B981',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: isSelected ? '#10B981' : '#FFFFFF' }}>
                      {site.site_name}
                    </Typography>
                    <Typography sx={{ fontSize: '10px', color: '#666666', mt: 0.25 }}>
                      Coordinates: {site.latitude.toFixed(4)}°N, {site.longitude.toFixed(4)}°E
                    </Typography>
                  </Box>
                  <LocationIcon sx={{ fontSize: 18, color: isSelected ? '#10B981' : '#555555' }} />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, my: 1.5, pt: 1, borderTop: '1px solid #1A1A1A' }}>
                  <Box>
                    <Typography sx={{ fontSize: '9px', color: '#555555', textTransform: 'uppercase' }}>Species</Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#10B981' }}>{site.totalSpecies}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '9px', color: '#555555', textTransform: 'uppercase' }}>Audios</Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#00D4FF' }}>{site.totalAudioFiles}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '9px', color: '#555555', textTransform: 'uppercase' }}>Deployments</Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#F59E0B' }}>{site.totalDeployments}</Typography>
                  </Box>
                </Box>

                <Button
                  size="small"
                  endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    width: '100%',
                    fontSize: '10px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    color: isSelected ? '#10B981' : '#888888',
                    bgcolor: isSelected ? 'rgba(16, 185, 129, 0.12)' : 'rgba(255, 255, 255, 0.03)',
                    border: `1px solid ${isSelected ? 'rgba(16, 185, 129, 0.3)' : '#222222'}`,
                    borderRadius: '6px',
                    py: 0.5,
                    '&:hover': {
                      bgcolor: '#10B981',
                      color: '#000000',
                    },
                  }}
                >
                  {isSelected ? 'Currently Selected' : 'View Site Analysis'}
                </Button>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectLevelSection;
